/**
 * Hash-free survey end links. Runs once, synchronously, from index.tsx before
 * anything else reads the URL.
 *
 * Our outcome pages are hash routes (#/surveys/completed, #/completed, ...), but
 * some client survey platforms drop the "#/" from the end links we give them,
 * so the respondent arrives on a real path instead:
 *
 *   /qualityterminate?userid=X                                  (Dive In)
 *   /surveys/overquota?surveyid=N&userid=X&ktr_supplier_...=T   (Kantar/nfield)
 *
 * HashRouter would settle on '/' and the outcome would never be recorded. We now
 * also publish the end links in this hash-free form, so every such path is sent
 * to the equivalent hash route with the query string carried across untouched.
 *
 * This is a browser-side redirect on purpose: outcomes must only ever be
 * recorded by a browser that runs our JS, never by a link preview crawler or an
 * email scanner that merely fetches the URL.
 *
 * A literal '#' never reaches the pathname, but an encoded one (%23) does when a
 * platform escapes our link, hence the optional '%23/' prefix.
 *
 * public/index.html runs the same redirect inline, ahead of the trackers; this
 * copy is the fallback. The test checks that the two agree.
 */
export const HASH_FREE_CALLBACK_PATH = /^\/(?:%23\/|#\/)?((?:surveys\/|thank-you\/)?(?:completed|terminate|overquota|qualityterminate))\/?$/i;

/**
 * Returns the hash-route URL to send the respondent to, or null when `pathname`
 * is not one of the outcome pages and the URL must be left alone.
 */
export function hashFreeCallbackRedirect(pathname: string, search: string): string | null {
    const match = HASH_FREE_CALLBACK_PATH.exec(pathname || '');
    return match ? '/#/' + match[1].toLowerCase() + (search || '') : null;
}
